/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import {
  Button,
  Table,
  Dropdown,
  Menu,
  Modal,
  DatePicker,
  Select,
  Input,
} from "antd";

import dayjs from "dayjs";

import { Eye, Settings, Trash2 } from "lucide-react";

import usePassengers from "../hooks/usePassengers";
import useParseString from "../hooks/useParseString";
import useCompanies from "../hooks/useCompanies";
import useHotels from "../hooks/useHotels";
import useUsers from "../hooks/useUsers";
import useExcursions from "../hooks/useExcursions";
import EmailParserModal from "./EmailParserModal";

const { RangePicker } = DatePicker;

const roleFormat = {
  SELLER: "Vendedor",
  GUIDE: "Guía",
  COORDINATOR: "Coordinador",
  OFFICE: "Oficina",
};

function PassengersTable() {
  const [params, setParams] = useState({
    page: 1,
    group__check_in_date_after: null,
    group__check_in_date_before: null,
    group__check_out_date_after: null,
    group__check_out_date_before: null,
    group__company: null,
    hotel: null,
    seller: null,
    scheduled_excursion: null,
    name: null,
  });
  const { passengers, getPassengers, deletePassenger, loading } =
    usePassengers();
  const { formatCurrency } = useParseString();
  const { getCompanies, companies } = useCompanies();
  const { getHotels, hotels } = useHotels();
  const { getUsers, users } = useUsers();
  const { getExcursions, excursions } = useExcursions();

  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [checkOutDateRange, setCheckOutDateRange] = useState(null);
  const [checkInDateRange, setCheckInDateRange] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedExcursion, setSelectedExcursion] = useState(null);
  const [nameFilter, setNameFilter] = useState(null);

  const showDeleteConfirm = (passenger) => {
    setSelectedPassenger(passenger);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    handleDelete(selectedPassenger.id);
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    getPassengers(params);
  }, [params]);

  useEffect(() => {
    getCompanies();
    getHotels();
    getUsers();
    getExcursions();
  }, []);

  const handleDelete = async (id) => {
    await deletePassenger(id);

    getPassengers(params);
  };

  const handleDateRangeChange = (dates, dateType) => {
    let newParams = { ...params };

    if (dateType === "checkOut") {
      if (dates) {
        // Format dates in YYYY-MM-DD format
        newParams.group__check_out_date_after = dates[0].format("YYYY-MM-DD");
        newParams.group__check_out_date_before = dates[1].format("YYYY-MM-DD");
        setCheckOutDateRange(dates);
      } else {
        // Clear date filters
        newParams.group__check_out_date_after = null;
        newParams.group__check_out_date_before = null;
        setCheckOutDateRange(null);
      }
    } else if (dateType === "checkIn") {
      if (dates) {
        // Format dates in YYYY-MM-DD format
        newParams.group__check_in_date_after = dates[0].format("YYYY-MM-DD");
        newParams.group__check_in_date_before = dates[1].format("YYYY-MM-DD");
        setCheckInDateRange(dates);
      } else {
        // Clear date filters
        newParams.group__check_in_date_after = null;
        newParams.group__check_in_date_before = null;
        setCheckInDateRange(null);
      }
    }

    setParams(newParams);
  };

  const handleCompanyChange = (value) => {
    if (value) {
      setSelectedCompany(value);
      setParams({
        ...params,
        group__company: value,
      });
    } else {
      setSelectedCompany(null);
      setParams({
        ...params,
        group__company: null,
      });
    }
  };

  const handleHotelChange = (value) => {
    if (value) {
      setSelectedHotel(value);
      setParams({
        ...params,
        hotel: value,
      });
    } else {
      setSelectedHotel(null);
      setParams({
        ...params,
        hotel: null,
      });
    }
  };

  const handleSellerChange = (value) => {
    if (value) {
      setSelectedSeller(value);
      setParams({
        ...params,
        seller: value,
      });
    } else {
      setSelectedSeller(null);
      setParams({
        ...params,
        seller: null,
      });
    }
  };

  const handleExcursionChange = (value) => {
    if (value) {
      setSelectedExcursion(value);
      setParams({
        ...params,
        scheduled_excursion: value,
      });
    } else {
      setSelectedExcursion(null);
      setParams({
        ...params,
        scheduled_excursion: null,
      });
    }
  };

  const handleNameChange = (value) => {
    if (value) {
      setNameFilter(value);
      setParams({
        ...params,
        name: value,
      });
    } else {
      setNameFilter(null);
      setParams({
        ...params,
        name: null,
      });
    }
  };

  const handleClearAllFilters = () => {
    setParams({
      ...params,
      group__check_in_date_after: null,
      group__check_in_date_before: null,
      group__check_out_date_after: null,
      group__check_out_date_before: null,
      group__company: null,
      hotel: null,
      seller: null,
      scheduled_excursion: null,
      name: null,
    });
    setCheckInDateRange(null);
    setCheckOutDateRange(null);
    setSelectedCompany(null);
    setSelectedHotel(null);
    setSelectedSeller(null);
    setSelectedExcursion(null);
    setNameFilter(null);
  };

  const hasActiveGroupFilter = () => {
    return (
      params.group__check_in_date_after !== null ||
      params.group__check_in_date_before !== null ||
      params.group__check_out_date_after !== null ||
      params.group__check_out_date_before !== null ||
      params.group__company !== null
    );
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 150,
      filteredValue: params.name ? ["filtered"] : null,
      filterDropdown: ({ confirm }) => (
        <div className="p-3" style={{ width: 250 }}>
          <div className="mb-4">
            <span className="mb-2 block font-medium">Filtro por nombre:</span>
            <Input
              placeholder="Buscar por nombre"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="w-full mb-2"
              allowClear
            />
            <div className="flex justify-between">
              <Button
                size="small"
                onClick={() => {
                  setNameFilter(null);
                  handleNameChange(null);
                  confirm();
                }}
                className="mr-1"
              >
                Limpiar
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => {
                  handleNameChange(nameFilter);
                  confirm();
                }}
              >
                Buscar
              </Button>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Hotel",
      dataIndex: "hotel",
      key: "hotel",
      width: 250,
      render: (hotel) => {
        return `${hotel.name} (${hotel.street} ${hotel.streetNumber})`;
      },
      filteredValue: params.hotel ? ["filtered"] : null,
      filterDropdown: ({ confirm }) => (
        <div className="p-3" style={{ width: 250 }}>
          <div className="mb-4">
            <span className="mb-2 block font-medium">Filtro por hotel:</span>
            <Select
              placeholder="Seleccionar hotel"
              onChange={handleHotelChange}
              value={selectedHotel}
              allowClear
              className="w-full mb-2"
              options={hotels.results?.map((hotel) => ({
                value: hotel.id,
                label: `${hotel.name}`,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp="label"
            />
            <Button
              size="small"
              onClick={() => {
                handleHotelChange(null);
                confirm();
              }}
              className="mr-1"
            >
              Limpiar
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => confirm()}
              className="float-right"
            >
              Aplicar
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Grupo",
      dataIndex: "group",
      key: "group",
      width: 250,
      render: (group) => {
        const parsedDate = dayjs(group.checkInDate).format("ddd DD MMM");
        return `${group.selectorDisplay} (${parsedDate})`;
      },
      filteredValue: hasActiveGroupFilter() ? ["filtered"] : null,
      filterDropdown: ({ confirm }) => (
        <div className="p-3" style={{ width: 300 }}>
          <div className="mb-4">
            <span className="mb-2 block font-medium">Filtro de empresa:</span>
            <Select
              placeholder="Seleccionar empresa"
              onChange={handleCompanyChange}
              value={selectedCompany}
              allowClear
              className="w-full mb-2"
              options={companies.results?.map((company) => ({
                value: company.id,
                label: company.name,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp="label"
            />
            <Button
              size="small"
              onClick={() => {
                handleCompanyChange(null);
              }}
              className="mr-1"
            >
              Limpiar
            </Button>
          </div>

          <div className="mb-4">
            <span className="mb-2 block font-medium">Fecha de entrada:</span>
            <RangePicker
              onChange={(dates) => {
                handleDateRangeChange(dates, "checkIn");
              }}
              value={checkInDateRange}
              format="DD/MM/YYYY"
              className="mb-2 w-full"
            />
            <Button
              size="small"
              onClick={() => {
                handleDateRangeChange(null, "checkIn");
              }}
              className="mr-1"
            >
              Limpiar
            </Button>
          </div>

          <div className="mb-4">
            <span className="mb-2 block font-medium">Fecha de salida:</span>
            <RangePicker
              onChange={(dates) => {
                handleDateRangeChange(dates, "checkOut");
              }}
              value={checkOutDateRange}
              format="DD/MM/YYYY"
              className="mb-2 w-full"
            />
            <Button
              size="small"
              onClick={() => {
                handleDateRangeChange(null, "checkOut");
              }}
              className="mr-1"
            >
              Limpiar
            </Button>
          </div>

          <div className="flex justify-between mt-2">
            <Button
              size="small"
              onClick={() => {
                handleClearAllFilters();
                confirm();
              }}
            >
              Limpiar todos
            </Button>
            <Button type="primary" size="small" onClick={() => confirm()}>
              Aplicar
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Excursion",
      dataIndex: "scheduledExcursion",
      key: "scheduledExcursion",
      width: 400,
      render: (scheduledExcursion) => {
        const parsedDate = dayjs(scheduledExcursion.day).format("ddd DD MMM");
        return `${scheduledExcursion.excursionType.name} (${parsedDate})`;
      },
      filteredValue: params.scheduled_excursion ? ["filtered"] : null,
      filterDropdown: ({ confirm }) => (
        <div className="p-3" style={{ width: 250 }}>
          <div className="mb-4">
            <span className="mb-2 block font-medium">
              Filtro por excursiones activas:
            </span>
            <Select
              placeholder="Seleccionar excursión"
              onChange={handleExcursionChange}
              value={selectedExcursion}
              allowClear
              className="w-full mb-2"
              options={excursions?.results?.map((excursion) => ({
                value: excursion.id,
                label: `${excursion.excursionType.name} (${dayjs(
                  excursion.day
                ).format("DD/MM/YYYY")})`,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp="label"
            />
            <Button
              size="small"
              onClick={() => {
                handleExcursionChange(null);
                confirm();
              }}
              className="mr-1"
            >
              Limpiar
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => confirm()}
              className="float-right"
            >
              Aplicar
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Cantidad",
      dataIndex: "quantitySimple",
      key: "quantitySimple",
      width: 100,
      align: "center",
    },
    {
      title: "Vendedor",
      dataIndex: "seller",
      key: "seller",
      width: 220,
      render: (seller) => {
        return `${seller.firstName} ${seller.lastName} (${
          roleFormat[seller.userType]
        })`;
      },
      filteredValue: params.seller ? ["filtered"] : null,
      filterDropdown: ({ confirm }) => (
        <div className="p-3" style={{ width: 250 }}>
          <div className="mb-4">
            <span className="mb-2 block font-medium">Filtro por vendedor:</span>
            <Select
              placeholder="Seleccionar vendedor"
              onChange={handleSellerChange}
              value={selectedSeller}
              allowClear
              className="w-full mb-2"
              options={users.map((user) => ({
                value: user.id,
                label: `${user.firstName} ${user.lastName} (${
                  roleFormat[user.userType] || user.userType
                })`,
              }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              optionFilterProp="label"
            />
            <Button
              size="small"
              onClick={() => {
                handleSellerChange(null);
                confirm();
              }}
              className="mr-1"
            >
              Limpiar
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => confirm()}
              className="float-right"
            >
              Aplicar
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: "Monto Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 150,
      align: "right",
      render: (totalAmount) => (
        <span style={{ color: (totalAmount || 0) >= 0 ? "green" : "red" }}>
          {formatCurrency(totalAmount || 0)}
        </span>
      ),
    },
    {
      title: "Total Pagado",
      dataIndex: "paymentAmount",
      key: "paymentAmount",
      width: 150,
      align: "right",
      render: (paymentAmount) => (
        <span style={{ color: (paymentAmount || 0) >= 0 ? "green" : "red" }}>
          {formatCurrency(paymentAmount || 0)}
        </span>
      ),
    },
    {
      title: "Deuda",
      key: "debt",
      fixed: "right",
      width: 120,
      align: "right",
      render: (_, record) => {
        const debt = (record.totalAmount || 0) - (record.paymentAmount || 0);
        return <span style={{ color: "red" }}>{formatCurrency(debt)}</span>;
      },
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      width: 50,
      render: (text, record) => {
        const items = [
          {
            key: "view",
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  navigate(`/pasajeros/${record.id}`);
                }}
              >
                <Eye size={16} className="me-2" />

                <span>Ver detalle</span>
              </div>
            ),
          },
          {
            key: "delete",
            danger: true,
            label: (
              <div
                className="flex items-center"
                onClick={() => {
                  showDeleteConfirm(record);
                }}
              >
                <Trash2 size={16} className="me-2" />
                <span>Borrar</span>
              </div>
            ),
          },
        ];

        return (
          <Dropdown overlay={<Menu items={items} />} trigger={["click"]}>
            <div className="flex justify-center">
              <Settings className="hover:cursor-pointer" size={16} />
            </div>
          </Dropdown>
        );
      },
    },
  ];

  const handleTableChange = (pagination) => {
    const newParams = {
      ...params,
      page: pagination,
    };
    setParams(newParams);
  };

  return (
    <div className="">
      <div className="flex justify-between">
        <span className="font-semibold text-lg">Listado de Pasajeros</span>

        <div className="flex gap-2">
          <EmailParserModal onSuccess={() => getPassengers(params)} />
          <Button
            type="primary"
            className="mb-3"
            onClick={() => {
              navigate("/pasajeros/crear");
            }}
          >
            Crear Pasajero
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={passengers.results}
        size="small"
        loading={loading.get}
        pagination={{
          current: params.page,
          pageSize: passengers.results?.length,
          total: passengers.count,
          onChange: handleTableChange,
        }}
        scroll={{ x: 1700 }}
        rowKey="id"
      />

      <Modal
        title="Confirmar eliminación"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okType="danger"
        okText="Borrar pasajero"
        confirmLoading={loading.delete}
        cancelText="Cancelar"
      >
        <p>¿Estás seguro de borrar este pasajero?</p>
        {selectedPassenger && (
          <div>
            <p>
              <strong>Nombre:</strong> {selectedPassenger.name}
            </p>
            <p>
              <strong>Grupo:</strong> {selectedPassenger.group.selectorDisplay}
            </p>
            <p>
              <strong>Hotel:</strong> {selectedPassenger.hotel.name}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default PassengersTable;
